/**
 * Bilt:Spec Passport - shared copy and label helpers.
 *
 * Premium/editorial tone, honest about proof strength. No XP/points/unlock
 * language anywhere in this module or its consumers.
 */
import type {
  PassportEncounterMethod,
  PassportEncounterStatus,
  PassportRiskStatus,
  PassportSubjectType,
  PassportVerificationLevel,
} from '../../services/api/passport';

export const VERIFICATION_LEVEL_LABELS: Record<PassportVerificationLevel, string> = {
  basic_qr: 'QR scanned',
  time_verified: 'Time-window verified',
  event_verified: 'Event verified',
  proximity_verified: 'Proximity verified',
  staff_verified: 'Staff verified',
  offline_pending: 'Pending validation',
  rejected_suspicious: 'Not verified',
};

export const STATUS_LABELS: Record<PassportEncounterStatus, string> = {
  pending: 'Pending validation',
  verified: 'Recorded',
  rejected: 'Not verified',
  revoked: 'Revoked',
};

export const SUBJECT_TYPE_LABELS: Record<PassportSubjectType, string> = {
  vehicle: 'Vehicle',
  event: 'Event',
  shop: 'Shop',
  club: 'Club',
  booth: 'Booth',
  activation: 'Activation',
  destination: 'Destination',
  place: 'Place',
  commemorative: 'Commemorative',
  checkpoint: 'Checkpoint',
};

export const METHOD_LABELS: Record<PassportEncounterMethod, string> = {
  qr_scan: 'QR scan',
  nfc_tap: 'NFC tap',
  manual_code: 'Manual code',
  geofence_checkin: 'Location check-in',
  organizer_grant: 'Organizer grant',
  system_grant: 'Platform grant',
};

/** Scan-token lifecycle, in owner language rather than the raw enum. */
export const SCAN_TOKEN_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  revoked: 'Revoked',
  expired: 'Expired',
};

export function verificationLevelLabel(
  level: PassportVerificationLevel | undefined | null
): string {
  if (!level) return 'Unverified';
  return VERIFICATION_LEVEL_LABELS[level] || level;
}

export function scanTokenStatusLabel(status: string | undefined | null): string {
  if (!status) return 'Unknown';
  return SCAN_TOKEN_STATUS_LABELS[status] || status;
}

export function encounterStatusLabel(status: PassportEncounterStatus | undefined | null): string {
  if (!status) return 'Unknown';
  return STATUS_LABELS[status] || status;
}

/** Tone for status badges - never color-only; always paired with text. */
export function encounterStatusTone(
  status: PassportEncounterStatus
): 'amber' | 'emerald' | 'red' | 'neutral' {
  switch (status) {
    case 'verified':
      return 'emerald';
    case 'pending':
      return 'amber';
    case 'rejected':
    case 'revoked':
      return 'red';
    default:
      return 'neutral';
  }
}

export function riskStatusLabel(risk: PassportRiskStatus | undefined | null): string {
  switch (risk) {
    case 'flagged':
      return 'Needs a look';
    case 'held':
      return 'Held for review';
    default:
      return 'Clear';
  }
}

/** Soft admin/ops copy — signals, not accusations. */
export function passportRiskTypeLabel(riskType: string | undefined | null): string {
  switch (riskType) {
    case 'geofence_mismatch':
      return 'Location signal inconsistent';
    case 'impossible_travel':
      return 'Location jump looks unlikely';
    case 'velocity_abuse':
      return 'Scan velocity elevated';
    case 'duplicate_device_farm':
      return 'Device pattern unusual';
    case 'token_reuse_conflict':
      return 'Token reuse conflict';
    case 'expired_token':
      return 'Expired token';
    case 'revoked_token':
      return 'Revoked token';
    case 'manual_flag':
      return 'Needs review';
    default:
      return riskType ? 'Needs review' : 'No extra signals';
  }
}

export function formatEncounterDate(value: string | null | undefined): string {
  if (!value) return 'Not set';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatEncounterDateTime(value: string | null | undefined): string {
  if (!value) return 'Not set';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const PASSPORT_DISABLED_TITLE = "Bilt:Spec Passport isn't available yet";
export const PASSPORT_DISABLED_DESCRIPTION =
  'Passport keeps cards from the builds, events and places you discover. Check back soon.';

export const passportScanSignInPrompt = 'Sign in to add this to your Passport';
export const passportScanNotCheckInNote =
  'This is a Passport encounter, not gate check-in or event admission.';
export const passportScanOwnBuildMessage =
  'Your own build is ready to share. Preview its card, then discover another build to grow your collection.';
export const passportScanDuplicateMessage = "You've already logged this one.";
export const passportScanPendingMessage = 'Logged. Pending validation.';
export const passportScanErrorFallback = 'Could not log that encounter';

export function passportScanSuccessMessage(
  subjectLabel: string,
  verificationLevel?: PassportVerificationLevel | null
): string {
  const level = verificationLevelLabel(verificationLevel);
  return `${subjectLabel} added to your Passport. ${level}.`;
}
